import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MccApi } from "lib/api/mccApiWrapper";
import type { SectorDTO, UserRole } from "models";
import { useSelector } from "react-redux";
import { UserSlice } from "store";
import type { MccUserDTO } from "auth";
import * as log from "lib/log";

export interface Sector {
	id: string;
	name: string;
}

/**
 * Custom hook for refreshing user permissions without reloading the page.
 * This should be used instead of window.location.reload() when handling 403 errors
 * or when permissions might have changed.
 *
 * @example
 * ```typescript
 * const { refreshPermissions, handlePermissionError } = UsePermissionRefresh();
 *
 * // Manual refresh
 * await refreshPermissions();
 *
 * // Handle 403 errors automatically
 * const handled = await handlePermissionError(error);
 * ```
 */
export function UsePermissionRefresh() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const refreshPermissions = useCallback(async (): Promise<boolean> => {
		try {
			const api = MccApi.GetInstance((window as any).store);
			const res = await api.Permissions();

			// Check if user has no menu items (no permissions)
			if (!res.user.menuList || res.user.menuList.length === 0) {
				log.Warn(
					"User has no permissions after refresh - redirecting to sector admin contact info"
				);
				navigate("/sector-admin-contact-info");
				return false;
			}

			// Update the store with new permissions
			dispatch(
				UserSlice.actions.setUserPermissions({
					menuList: res.user.menuList,
					permissionList: res.permissionList,
					user: res.user,
					userRoles: res.getUserRoles,
				})
			);

			log.Info("Permissions refreshed successfully");
			return true;
		} catch (err: any) {
			// Any error during permission refresh should redirect to sector admin contact info
			navigate("/sector-admin-contact-info");
			return false;
		}
	}, [dispatch, navigate]);

	const handlePermissionError = useCallback(
		async (error: any): Promise<boolean> => {
			const status = error?.response?.status || error?.status;

			if (status === 403) {
				log.Info(
					"403 error detected, attempting to refresh permissions"
				);
				return await refreshPermissions();
			}

			// Not a permission error, let the calling code handle it
			return false;
		},
		[refreshPermissions]
	);

	return {
		refreshPermissions,
		handlePermissionError,
	};
}

export function UseSectorsAndPermissions() {
	const [sectors, setSectors] = useState<Sector[]>([]);
	const [allPlantTypes, setAllPlantTypes] = useState<string[]>([]);
	const [userPlantTypes, setUserPlantTypes] = useState<string[]>([]);
	const [loadingSectors, setLoadingSectors] = useState(false);
	const [error, setError] = useState<string>("");

	// Pull already-fetched permissions data from the slice
	const sliceUserRoles = useSelector(
		UserSlice.selectors.selectUserRoles
	) as UserRole[];
	const sliceUser = useSelector(UserSlice.selectors.selectUser) as
		| (MccUserDTO & { sectorList?: SectorDTO[] })
		| null;
	const permsLoading = useSelector(UserSlice.selectors.selectLoading);

	// Use permission refresh hook
	const { handlePermissionError } = UsePermissionRefresh();

	// Fetch ALL sectors (full list)
	useEffect(() => {
		(async () => {
			try {
				setLoadingSectors(true);
				setError("");
				const api = MccApi.GetInstance((window as any).store);
				const sectorsResp: Sector[] = await api.ContainmentSectors();
				setSectors(sectorsResp);
				setAllPlantTypes(
					sectorsResp.map((a) => a.name).filter(Boolean)
				);
			} catch (e: any) {
				// Try to refresh permissions instead of reloading the page
				try {
					await handlePermissionError(e);
				} catch (retryError: any) {
					setError(
						retryError?.response?.data?.message ||
							retryError?.message ||
							"Failed to load sectors."
					);
					setSectors([]);
					setAllPlantTypes([]);
				}
			} finally {
				setLoadingSectors(false);
			}
		})();
	}, []);

	// Derive user plant types from slice (no extra API call)
	useEffect(() => {
		const fromUser = (sliceUser?.sectorList || [])
			.map((a) => a.name)
			.filter(Boolean);
		const fromSliceContainment = (sliceUserRoles || [])
			.map((a) => a.containmentSectorName)
			.filter(Boolean);
		const resolved =
			(fromUser.length && fromUser) ||
			(fromSliceContainment.length && fromSliceContainment) ||
			allPlantTypes;
		setUserPlantTypes(resolved);
	}, [sliceUser, sliceUserRoles, allPlantTypes]);

	const combinedLoading = loadingSectors || permsLoading;

	return {
		sectors,
		allPlantTypes,
		userPlantTypes,
		loadingSectors: combinedLoading,
		error,
	};
}
